import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from '../../lib/auth-context.js';
import { useLicense } from '../common/license-context.js';
import {
  DEMO_KPI_KEYS,
  DEMO_KPI_METRICS,
  DEMO_KPI_TYPES,
  DEMO_KPI_VALUES_ITEMS,
  toEnvelope,
  useDemoMode,
  useDemoOrLive,
} from '../demo/index.js';

/**
 * KPI hooks. Each maps to a `/v1/kpi/*` proxy route on app-api,
 * which forwards to the engine. Engine wraps every KPI response in
 * `{ data: Row[] }`; hooks unwrap to a bare array so consumers get
 * rows directly.
 *
 * Mutations (create/update/delete) invalidate the full `kpi` query
 * family so any consumer (value page, dashboard tile, scenario
 * detail modal) refreshes without a full reload.
 */

type Row = Record<string, unknown>;

function unwrapData<T>(r: { data: T }): T {
  return r.data;
}

export function useKpiTypes(): { data?: Row[]; isLoading: boolean; error: Error | null } {
  const { session, api } = useAuth();
  const q = useQuery({
    queryKey: ['kpi', 'types', session?.licenseId ?? 'anon'],
    queryFn: () => api.get<{ data: Row[] }>('/v1/kpi/types').then(unwrapData),
    enabled: !!session,
    staleTime: 60_000,
  });
  const env = useDemoOrLive<Row[]>(DEMO_KPI_TYPES as Row[], toEnvelope(q));
  return { data: env.data, isLoading: env.isLoading, error: env.error };
}

export function useKpiMetrics(): { data?: Row[]; isLoading: boolean; error: Error | null } {
  const { session, api } = useAuth();
  const q = useQuery({
    queryKey: ['kpi', 'metrics', session?.licenseId ?? 'anon'],
    queryFn: () => api.get<{ data: Row[] }>('/v1/kpi/metrics').then(unwrapData),
    enabled: !!session,
    staleTime: 60_000,
  });
  const env = useDemoOrLive<Row[]>(DEMO_KPI_METRICS as Row[], toEnvelope(q));
  return { data: env.data, isLoading: env.isLoading, error: env.error };
}

export function useKpiKeys(): { data?: Row[]; isLoading: boolean; error: Error | null } {
  const { session, api } = useAuth();
  const q = useQuery({
    queryKey: ['kpi', 'keys', session?.licenseId ?? 'anon'],
    queryFn: () => api.get<{ data: Row[] }>('/v1/kpi/keys').then(unwrapData),
    enabled: !!session,
    staleTime: 60_000,
  });
  const env = useDemoOrLive<Row[]>(DEMO_KPI_KEYS as Row[], toEnvelope(q));
  return { data: env.data, isLoading: env.isLoading, error: env.error };
}

/**
 * Per-scenario, per-metric KPI counters aggregated by date bucket.
 * Wraps `GET /v1/kpi/values` (proxied to `/engine/v1/kpi-values`),
 * which reads from the `wf-coe-{env}-kpi` DDB table.
 *
 * Returns the raw wire shape — see `KpiValueItem` for the per-item
 * structure (one item per `(fusionScenarioId, datePart)` tuple, with
 * a `metrics: Record<metricId, {value, quantity, count}>` map). The
 * `rollupByScenarioMetric` helper below collapses the date dimension
 * into a per-(scenario, metric) total when callers don't need the
 * time-series.
 *
 * `from`/`to` are date strings the engine accepts
 * (`YYYY-MM-DDTHH | YYYY-MM-DD | YYYY-MM`).
 */
export interface KpiMetricCounters {
  value?: number;
  quantity?: number;
  count?: number;
}

export interface KpiValueItem {
  fusionScenarioId: string;
  datePart: string;
  metrics: Record<string, KpiMetricCounters>;
}

export interface UseKpiValuesParams {
  from: string;
  to: string;
  aggregationType: 'hour' | 'day' | 'month';
  fusionScenarioId?: string;
}

export function useKpiValues(params: UseKpiValuesParams): {
  data?: ReadonlyArray<KpiValueItem>;
  isLoading: boolean;
  error: Error | null;
} {
  const { session, api } = useAuth();
  const qs = new URLSearchParams();
  qs.set('from', params.from);
  qs.set('to', params.to);
  qs.set('aggregation-type', params.aggregationType);
  if (params.fusionScenarioId) qs.set('fusionScenarioId', params.fusionScenarioId);
  const q = useQuery({
    queryKey: [
      'kpi',
      'values',
      session?.licenseId ?? 'anon',
      params.from,
      params.to,
      params.aggregationType,
      params.fusionScenarioId ?? null,
    ],
    queryFn: () =>
      api
        .get<{ data: KpiValueItem[] }>(`/v1/kpi/values?${qs.toString()}`)
        .then(unwrapData)
        .then((items) => items ?? []),
    enabled: !!session,
    staleTime: 60_000,
  });
  // Demo mode: a 30-day daily fixture matching the engine wire shape.
  const env = useDemoOrLive<ReadonlyArray<KpiValueItem>>(
    DEMO_KPI_VALUES_ITEMS as ReadonlyArray<KpiValueItem>,
    toEnvelope(q),
  );
  return { data: env.data, isLoading: env.isLoading, error: env.error };
}

/**
 * Resolve the "effective" main-metric id used by the value rollup
 * surfaces (Dashboard Cost Saving, Scenarios By Value, Scenario
 * KPIs By Amount, Presentation detail KPI table, etc).
 *
 * Precedence:
 *   1. License's `mainMetricId` if set.
 *   2. In demo mode, the first metric id from the catalog — keeps
 *      showcase surfaces populated even when the real license's
 *      `mainMetricId` is null or points outside the demo fixture's
 *      id range (`201`–`211`).
 *   3. Otherwise `null` — caller is expected to handle the "no
 *      main metric" case (typically: sum across all metrics in the
 *      rollup, see PR #107).
 *
 * Co-located with the KPI hooks because every consumer pairs this
 * with `useKpiValues` + `rollupByScenarioMetric` on the same page.
 */
export function useEffectiveMainMetricId(): string | null {
  const { details: license } = useLicense();
  const { isDemoMode } = useDemoMode();
  const { data: metricRows = [] } = useKpiMetrics();

  if (license?.mainMetricId) return String(license.mainMetricId);
  if (isDemoMode && metricRows.length > 0) {
    const first = metricRows[0];
    const id = typeof first?.id === 'number' ? first.id : null;
    return id !== null ? String(id) : null;
  }
  return null;
}

/**
 * Map a `PresentationDateRangePreset` (or any of the legacy preset
 * names) to an inclusive `[from, to]` pair of `YYYY-MM-DD` strings,
 * relative to `today` (defaults to "now"). Useful for the Dashboard
 * + Value pages where the operator picks a range from a dropdown
 * and the SPA passes it to `useKpiValues`.
 *
 * `'custom'` returns a 30-day window — the caller is expected to
 * override it when wiring a real custom-range picker; until that
 * picker lands, 30-day is the closest stand-in.
 */
export type DateRangePreset =
  | 'today'
  | 'yesterday'
  | 'this-week'
  | 'last-week'
  | 'this-month'
  | 'last-month'
  | 'last-7-days'
  | 'last-30-days'
  | 'custom';

export function presetToDateRange(
  preset: DateRangePreset,
  today: Date = new Date(),
): { from: string; to: string } {
  const t = new Date(today);
  t.setUTCHours(0, 0, 0, 0);
  const isoDay = (d: Date): string => d.toISOString().slice(0, 10);
  const shift = (d: Date, days: number): Date => {
    const out = new Date(d);
    out.setUTCDate(out.getUTCDate() + days);
    return out;
  };
  const startOfWeek = (d: Date): Date => {
    // ISO week — Monday-first to match legacy.
    const out = new Date(d);
    const day = out.getUTCDay();
    const diff = (day === 0 ? -6 : 1) - day;
    return shift(out, diff);
  };
  const startOfMonth = (d: Date): Date => {
    const out = new Date(d);
    out.setUTCDate(1);
    return out;
  };
  switch (preset) {
    case 'today':
      return { from: isoDay(t), to: isoDay(t) };
    case 'yesterday': {
      const y = shift(t, -1);
      return { from: isoDay(y), to: isoDay(y) };
    }
    case 'this-week':
      return { from: isoDay(startOfWeek(t)), to: isoDay(t) };
    case 'last-week': {
      const start = shift(startOfWeek(t), -7);
      const end = shift(start, 6);
      return { from: isoDay(start), to: isoDay(end) };
    }
    case 'this-month':
      return { from: isoDay(startOfMonth(t)), to: isoDay(t) };
    case 'last-month': {
      const thisStart = startOfMonth(t);
      const lastStart = new Date(thisStart);
      lastStart.setUTCMonth(lastStart.getUTCMonth() - 1);
      const lastEnd = shift(thisStart, -1);
      return { from: isoDay(lastStart), to: isoDay(lastEnd) };
    }
    case 'last-7-days':
      return { from: isoDay(shift(t, -6)), to: isoDay(t) };
    case 'last-30-days':
    case 'custom':
      return { from: isoDay(shift(t, -29)), to: isoDay(t) };
  }
}

/**
 * Collapse the date dimension of a `useKpiValues` payload into a
 * per-scenario × per-metric total. Returns a 2-level map:
 *   `Map<fusionScenarioId, Map<metricId, {value, quantity, count}>>`
 * where every counter is summed across the date buckets covered by
 * the input items. Missing counters default to 0.
 *
 * Pure — safe to call inside `useMemo`.
 */
export interface KpiCounterTotals {
  value: number;
  quantity: number;
  count: number;
}

export function rollupByScenarioMetric(
  items: ReadonlyArray<KpiValueItem>,
): Map<string, Map<string, KpiCounterTotals>> {
  const out = new Map<string, Map<string, KpiCounterTotals>>();
  for (const it of items) {
    const byMetric = out.get(it.fusionScenarioId) ?? new Map<string, KpiCounterTotals>();
    for (const [metricId, counters] of Object.entries(it.metrics)) {
      const totals = byMetric.get(metricId) ?? { value: 0, quantity: 0, count: 0 };
      totals.value += counters.value ?? 0;
      totals.quantity += counters.quantity ?? 0;
      totals.count += counters.count ?? 0;
      byMetric.set(metricId, totals);
    }
    out.set(it.fusionScenarioId, byMetric);
  }
  return out;
}

/* ── Mutations ──
 *
 * Engine schemas: `packages/types/src/kpi-{type,metric}.ts`
 *   CreateKpiTypeSchema:   { name, color, icon }                    (.strict)
 *   UpdateKpiTypeSchema:   same shape (full-replace)
 *   CreateKpiMetricSchema: { displayName, internalName?, color, icon, kpiTypeId? }
 *   UpdateKpiMetricSchema: { displayName, internalName (required), color, icon, kpiTypeId? }
 *
 * Engine + app-api both validate; the hook input types only set the
 * happy-path field set, mirroring the schemas above.
 */

export interface KpiTypeInput {
  name: string;
  color: string;
  icon: string;
}

export interface KpiMetricInput {
  displayName: string;
  internalName?: string;
  color: string;
  icon: string;
  kpiTypeId?: number;
}

/** Engine accepts the legacy key shape verbatim. Kept loose because
 *  the SPA only authors scenario id + metric id pairs today; engine
 *  owns validation. */
export type KpiKeyInput = Record<string, unknown>;

interface MutationResult<TArgs> {
  mutate: (args: TArgs) => Promise<unknown>;
  isPending: boolean;
  error: Error | null;
}

/* ── KPI Types ── */

export function useCreateKpiType(): MutationResult<KpiTypeInput> {
  const { session, api } = useAuth();
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (input: KpiTypeInput) => api.post<unknown>('/v1/kpi/types', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['kpi'] });
      toast.success('KPI type added');
    },
    onError: (err: Error) => toast.error(`Could not add KPI type: ${err.message}`),
  });
  return {
    mutate: (input) => m.mutateAsync(input),
    isPending: m.isPending,
    error: (m.error as Error) ?? null,
  };
}

export function useUpdateKpiType(): MutationResult<{ id: number; input: KpiTypeInput }> {
  const { api } = useAuth();
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: ({ id, input }: { id: number; input: KpiTypeInput }) =>
      api.put<unknown>(`/v1/kpi/types/${encodeURIComponent(id)}`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['kpi'] });
    },
    onError: (err: Error) => toast.error(`Could not save KPI type: ${err.message}`),
  });
  return {
    mutate: (args) => m.mutateAsync(args),
    isPending: m.isPending,
    error: (m.error as Error) ?? null,
  };
}

export function useDeleteKpiType(): MutationResult<number> {
  const { api } = useAuth();
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (id: number) => api.delete<unknown>(`/v1/kpi/types/${encodeURIComponent(id)}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['kpi'] });
      toast.success('KPI type deleted');
    },
    onError: (err: Error) => toast.error(`Could not delete KPI type: ${err.message}`),
  });
  return {
    mutate: (id) => m.mutateAsync(id),
    isPending: m.isPending,
    error: (m.error as Error) ?? null,
  };
}

/* ── KPI Metrics ── */

export function useCreateKpiMetric(): MutationResult<KpiMetricInput> {
  const { api } = useAuth();
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (input: KpiMetricInput) => api.post<unknown>('/v1/kpi/metrics', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['kpi'] });
      toast.success('KPI metric added');
    },
    onError: (err: Error) => toast.error(`Could not add KPI metric: ${err.message}`),
  });
  return {
    mutate: (input) => m.mutateAsync(input),
    isPending: m.isPending,
    error: (m.error as Error) ?? null,
  };
}

export function useUpdateKpiMetric(): MutationResult<{ id: number; input: KpiMetricInput }> {
  const { api } = useAuth();
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: ({ id, input }: { id: number; input: KpiMetricInput }) =>
      api.put<unknown>(`/v1/kpi/metrics/${encodeURIComponent(id)}`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['kpi'] });
    },
    onError: (err: Error) => toast.error(`Could not save KPI metric: ${err.message}`),
  });
  return {
    mutate: (args) => m.mutateAsync(args),
    isPending: m.isPending,
    error: (m.error as Error) ?? null,
  };
}

export function useDeleteKpiMetric(): MutationResult<number> {
  const { api } = useAuth();
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (id: number) => api.delete<unknown>(`/v1/kpi/metrics/${encodeURIComponent(id)}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['kpi'] });
      // The FK sets `licenses.main_metric_id` to NULL when the main metric is
      // deleted, so the cached license is stale until refetched.
      void qc.invalidateQueries({ queryKey: ['license'] });
      toast.success('KPI metric deleted');
    },
    onError: (err: Error) => toast.error(`Could not delete KPI metric: ${err.message}`),
  });
  return {
    mutate: (id) => m.mutateAsync(id),
    isPending: m.isPending,
    error: (m.error as Error) ?? null,
  };
}

/* ── KPI Keys (scenario ↔ metric attachments) ── */

export function useCreateKpiKey(): MutationResult<KpiKeyInput> {
  const { api } = useAuth();
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (input: KpiKeyInput) => api.post<unknown>('/v1/kpi/keys', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['kpi'] });
    },
    onError: (err: Error) => toast.error(`Could not attach KPI metric: ${err.message}`),
  });
  return {
    mutate: (input) => m.mutateAsync(input),
    isPending: m.isPending,
    error: (m.error as Error) ?? null,
  };
}

export function useUpdateKpiKey(): MutationResult<{ id: number; input: KpiKeyInput }> {
  const { api } = useAuth();
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: ({ id, input }: { id: number; input: KpiKeyInput }) =>
      api.put<unknown>(`/v1/kpi/keys/${encodeURIComponent(id)}`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['kpi'] });
      // Also nudge the scenarios list — its `kpiCount` per row is
      // derived from the kpi-key count on the engine side; a
      // re-fetch is the cheapest way to keep the badge accurate.
      void qc.invalidateQueries({ queryKey: ['scenarios', 'list'] });
    },
    onError: (err: Error) => toast.error(`Could not update KPI attachments: ${err.message}`),
  });
  return {
    mutate: ({ id, input }) => m.mutateAsync({ id, input }),
    isPending: m.isPending,
    error: (m.error as Error) ?? null,
  };
}

export function useDeleteKpiKey(): MutationResult<number> {
  const { api } = useAuth();
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (id: number) => api.delete<unknown>(`/v1/kpi/keys/${encodeURIComponent(id)}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['kpi'] });
    },
    onError: (err: Error) => toast.error(`Could not detach KPI metric: ${err.message}`),
  });
  return {
    mutate: (id) => m.mutateAsync(id),
    isPending: m.isPending,
    error: (m.error as Error) ?? null,
  };
}
